/**
 * Chat WebSocket hook — connect, auth, dispatch events tới Zustand store.
 */
import * as React from "react";
import ReconnectingWebSocket from "reconnecting-websocket";

import { useAuthStore } from "@/stores/auth";
import type { ActiveWidget } from "@/stores/chat";
import { useChatStore } from "@/stores/chat";
import type { Message, Thought, Uuid, WidgetType, WsCommand, WsEvent } from "@/types";

const WS_BASE = import.meta.env.VITE_WS_BASE_URL ?? "ws://localhost:8002";

export interface UseChatWebSocketResult {
  send: (cmd: WsCommand) => void;
  isReady: boolean;
}

export function useChatWebSocket(conversationId: Uuid | null): UseChatWebSocketResult {
  const accessToken = useAuthStore((s) => s.accessToken);
  const wsRef = React.useRef<ReconnectingWebSocket | null>(null);
  const [isReady, setIsReady] = React.useState(false);

  const {
    appendMessage,
    appendDelta,
    finishMessage,
    addThought,
    addWidget,
    setConnectionStatus,
  } = useChatStore();

  React.useEffect(() => {
    if (!conversationId || !accessToken) return;

    const url = `${WS_BASE}/ws/chat?conv_id=${conversationId}`;
    setConnectionStatus("connecting");

    const ws = new ReconnectingWebSocket(url, [], {
      maxRetries: 10,
      connectionTimeout: 4000,
      maxReconnectionDelay: 10000,
      minReconnectionDelay: 1000,
      reconnectionDelayGrowFactor: 1.3,
    });
    wsRef.current = ws;

    ws.addEventListener("open", () => {
      ws.send(JSON.stringify({ type: "auth", token: accessToken }));
    });

    ws.addEventListener("close", () => {
      setConnectionStatus("closed");
      setIsReady(false);
    });

    ws.addEventListener("error", () => {
      setConnectionStatus("reconnecting");
    });

    ws.addEventListener("message", (e) => {
      let event: WsEvent;
      try {
        event = JSON.parse(e.data) as WsEvent;
      } catch {
        return;
      }

      switch (event.type) {
        case "connected":
          setConnectionStatus("connected");
          setIsReady(true);
          break;

        case "message_started": {
          const placeholder: Message = {
            id: event.message_id,
            conversation_id: conversationId,
            role: "assistant",
            content: { type: "text", text: "" },
            metadata: { agent_run_id: event.agent_run_id },
            created_at: new Date().toISOString(),
          };
          appendMessage(placeholder);
          break;
        }

        case "thought": {
          const thought: Thought = {
            id: `th_${Date.now()}_${Math.random().toString(36).slice(2)}`,
            step: event.step,
            tool: event.tool,
            startedAt: Date.now(),
            status: "running",
          };
          addThought(thought);
          break;
        }

        case "text_delta":
          appendDelta(event.message_id, event.delta);
          break;

        case "widget": {
          const widget: ActiveWidget = {
            message_id: event.message_id,
            widget_id: event.widget_id,
            widget_type: event.widget_type as WidgetType,
            payload: event.payload as Record<string, unknown>,
            status: "pending",
          };
          addWidget(widget);
          // Cũng cập nhật message content thành widget
          useChatStore.setState((s) => {
            const idx = s.messages.findIndex((m) => m.id === event.message_id);
            if (idx === -1) return s;
            const next = [...s.messages];
            const target = next[idx];
            if (!target) return s;
            next[idx] = {
              ...target,
              content: {
                type: "widget",
                widget_type: event.widget_type as WidgetType,
                payload: event.payload as Record<string, unknown>,
              },
              metadata: { ...target.metadata, widget_id: event.widget_id },
            };
            return { messages: next };
          });
          break;
        }

        case "message_finished":
          finishMessage(event.message_id);
          break;

        case "error":
          // eslint-disable-next-line no-console
          console.error("ws error:", event.code, event.message);
          useChatStore.setState((s) => {
            const last = s.messages[s.messages.length - 1];
            if (last && last.role === "assistant" && last.content.type === "text") {
              const next = [...s.messages];
              next[next.length - 1] = {
                ...last,
                content: { type: "text", text: `❌ ${event.message}` },
              };
              return { messages: next };
            }
            return s;
          });
          break;

        case "pong":
          break;
      }
    });

    const pingInterval = window.setInterval(() => {
      if (ws.readyState === ReconnectingWebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "ping" }));
      }
    }, 30_000);

    return () => {
      window.clearInterval(pingInterval);
      ws.close();
      wsRef.current = null;
      setIsReady(false);
    };
  }, [
    conversationId,
    accessToken,
    appendDelta,
    appendMessage,
    addThought,
    addWidget,
    finishMessage,
    setConnectionStatus,
  ]);

  const send = React.useCallback((cmd: WsCommand) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === ReconnectingWebSocket.OPEN) {
      ws.send(JSON.stringify(cmd));
    }
  }, []);

  return { send, isReady };
}
